import React from 'react';
import useAppContext from '../../../context/useAppContext';

import { Link, useHistory } from 'react-router-dom';
import {
    WeezHeader,
    Button,
    Tab,
    Table,
    SelectButton,
    Dropdown,
    DropdownItem,
    Card,
    ContentCard,
    Icon,
    Paginator,
    TableCellDropdown,
    Numbers,
    ButtonDropdown,
    Message,
    FilterSetController,
    Filter,
    ToastContext,
    StopPropagation
} from '@weezevent/nacre';
import Order from '../models/orderModel';
import { Trans, useTranslation } from 'react-i18next';
import { handleSearchQuery, useFilters, useOriginDetails, useCrossSelling, useManagerContext, useCurrentPage, usePageSize, useSort } from '../../manager/components/context';
import Style from './list.module.css';
import DropdownPageSize from '../../manager/components/dropdownPageSize';
import { AttendeeDate as OrderDate, InfoTextToolTipMemo } from '../../attendee/list/components/attendee-row/attendee-row';
import { OrderStatus } from '../detail/detail';
import ActionContainer from '../action-container/actionContainer';
import ChartedEvent from '../../../components/chartedEvent';
import useMixpanel from '../../../context/useMixpanel';
import { optionsOrigin, initialFilters, LabelEndDateFilter } from '../../attendee/list/list';
import i18n from 'i18next';
import SortDropdown from '../../../components/sortDropdown';
import useFirstRender from '../../../helpers/useFirstRender';
import UnpaidAction from '../actions/unpaidAction';
import { ActionDropdownItem } from '../action-dropdown-item/actionDropdownItem';
import PaidAction from '../actions/paidAction';
import DeleteAction from '../actions/deleteAction';
import AnonymizeAction from '../actions/anonymizeAction';
import DownloadTicketsAction from '../actions/downloadTicketsActions';
import DownloadInvoicesAction from '../actions/downloadInvoicesAction';
import SendReceiptAction from '../actions/sendReceiptAction';
import LinkPaymentAction from '../actions/linkPaymentAction';
import { SendPaymentRequest, SendUpdateInfoRequest } from '../actions/sendRequest';
import SendPaymentInstructionAction from '../actions/sendPaymentInstructionAction';
import RestoreAction from '../actions/restoreAction';
import SendInvoice from '../actions/sendInvoice';
import RotateBarcodeOrder from '../actions/rotateBarcode';
import Refund from '../actions/refund';
import DeleteRefundRequestAction from '../actions/deleteRefundRequestAction';
import { AttendeeExportModal, OrderExportModal } from '../../attendee/list/components/export-modal';
import SendApprovedTicketsToBuyerAction from '../actions/sendApprovedTicketsToBuyerAction';
import { useSegment, useTrackingPage } from '../../../components/tracking/trackingProvider';
import LoadingTable from '../../../components/loadingTable';
import { capitalizeFirstLetter } from '../../attendee/detail/components/utils';
import { updateContextPagination } from '../../../models/paginatedModel';
import { ColumnsConfigurator, ColumnsConfiguratorTrigger } from '@weezevent/smart-table';
import { Columns } from '../constants';
import { useIsEmailingBlocked } from '../../../helpers/useIsEmailingBlocked';
import { useColumns } from './useColumns';

export const initAction = {
    action: null,
    items: null
};

export const actionList = [
    SendReceiptAction,
    DownloadTicketsAction,
    DownloadInvoicesAction,
    SendApprovedTicketsToBuyerAction,
    SendInvoice,
    PaidAction,
    UnpaidAction,
    SendPaymentInstructionAction,
    SendPaymentRequest,
    SendUpdateInfoRequest,
    RotateBarcodeOrder,
    Refund,
    RestoreAction,
    DeleteAction,
    DeleteRefundRequestAction,
    AnonymizeAction
];

export const PaymentTypesList = ['VISA_MASTERCARD', 'AMERICAN_EXPRESS', 'ONLINE', 'PAY_OVER_TIME', 'PAYPAL', 'MBWAY', 'PAYMENT_PLAN', 'EMPTY'];

export const GetPaymentTypeLabel = (paymentType, t, include_empty = false) => {
    if (!include_empty && (paymentType === 'EMPTY' || !PaymentTypesList.includes(paymentType))) {
        return '';
    } else if (include_empty && paymentType === 'EMPTY') {
        return t(`paymentinvoicing.payment_types.${paymentType.toLowerCase()}`);
    }
    return t(`paymentinvoicing.payment_types.${paymentType.toLowerCase()}`);
};

export const ListDropdownItems = React.memo(({ order, paymentPlan = null, onChangeAction, mode = 'list' }) => {
    const isEmailingBlocked = useIsEmailingBlocked();
    const dropdownActionList = actionList.map(modelAction => (
        <ActionDropdownItem key={modelAction.NAME} mode={mode} modelAction={modelAction} onChangeAction={onChangeAction} items={[order]} isEmailingBlocked={isEmailingBlocked} />
    ));
    if (mode === 'detail' && paymentPlan) {
        dropdownActionList.splice(13, 0, <LinkPaymentAction key="payment" order={order} paymentPlan={paymentPlan} />);
    }
    return dropdownActionList;
});

const OrderRow = React.memo(({ index, order, onSelected = () => {}, isChecked = false, onChangeAction = () => {}, columns = [] }) => {
    const { i18n, t } = useTranslation();
    const { track } = useSegment();

    const [{ event, organization, feature_flags }] = useAppContext();
    const history = useHistory();

    const prefix = React.useMemo(() => {
        return `/ticket/O${organization.id}/E${event.id}/manager/orders`;
    }, [event, organization]);

    const orderSubtitle = React.useMemo(() => {
        if ((order.origin === 'weez_manual_add' && Boolean(order.origin_detail)) || order.origin === 'weez_distributor') {
            return capitalizeFirstLetter(order.origin_detail);
        }

        return t(`ticket.manager.orders.origin.${order.origin}`);
    }, [order]);

    const cells = {
        [Columns.SOLD_DATE]: () => (
            <Table.Td className={`${Style['width-small']} ${Style['list__td-info-text']}`}>
                <OrderDate date={order.sold_date} />
            </Table.Td>
        ),
        [Columns.BUYER]: () => (
            <Table.Td className={`${Style['width-x-large']} ${Style['list__td-info-text']}`}>
                <InfoTextToolTipMemo tooltipCopy title={order.resolved_buyer_name} subtitle={order.buyer_email} />
            </Table.Td>
        ),
        [Columns.ORDER_ID]: () => <Table.Td className={Style['width-x-mini']}>{order.id_transaction || '-'}</Table.Td>,
        [Columns.ORDER]: () => (
            <Table.Td className={`${Style['width-regular']} ${Style['list__td-info-text']}`}>
                <InfoTextToolTipMemo tooltipCopy title={order.transaction_reference} subtitle={orderSubtitle} />
            </Table.Td>
        ),
        [Columns.QUANTITY]: () => <Table.Td className={`${Style['width-mini']} ${Style['list__td-info-text']}`}>{order.quantity}</Table.Td>,
        [Columns.BUYING_PRICE]: () => (
            <Table.Td className={`${Style['width-mini']} ${Style['list__td-info-text']}`}>
                <InfoTextToolTipMemo
                    title={<Numbers value={order.price_ticket} locale={i18n.language} currency={organization.currency} showNullDecimal />}
                    subtitle={order.internal_transaction ? t('ticket.common.paymentMethod.online_payment') : null}
                />
            </Table.Td>
        ),
        [Columns.PAYMENT_TYPE]: () => (
            <Table.Td className={`${Style['width-mini']} ${Style['list__td-info-text']}`}>
                {order.origin === 'weez_web' && order.price_ticket > 0 ? GetPaymentTypeLabel(order.payment_type, t) : '-'}
            </Table.Td>
        ),
        [Columns.LANGUAGE]: () => <Table.Td className={Style['width-x-mini']}>{order.locale || '-'}</Table.Td>,
        [Columns.STATUS]: () => (
            <Table.Td className={Style['width-x-mini']}>
                <StopPropagation>
                    <OrderStatus
                        order={order}
                        list
                        items={[
                            <DropdownItem
                                key={PaidAction.NAME}
                                disabled={PaidAction.is_disabled({ items: [order], feature_flags })}
                                item={t('ticket.manager.' + PaidAction.TYPE + '.action.' + PaidAction.NAME + '.dropdownItem', { count: [order].length })}
                                deleteItem={false}
                                onClick={() => {
                                    track('ACTION > SWITCH STATUS', { 'Page Title': 'MANAGE ORDERS', 'Order status': 'Paid' });
                                    onChangeAction({ action: PaidAction, items: [order] });
                                }}
                            />,
                            <DropdownItem
                                key={UnpaidAction.NAME}
                                disabled={UnpaidAction.is_disabled({ items: [order], feature_flags })}
                                item={t('ticket.manager.' + UnpaidAction.TYPE + '.action.' + UnpaidAction.NAME + '.dropdownItem', { count: [order].length })}
                                deleteItem={false}
                                onClick={() => {
                                    track('ACTION > SWITCH STATUS', { 'Page Title': 'MANAGE ORDERS', 'Order status': 'Unpaid' });
                                    onChangeAction({ action: UnpaidAction, items: [order] });
                                }}
                            />
                        ]}
                    />
                </StopPropagation>
            </Table.Td>
        ),
        [Columns.ACTIONS]: () => (
            <TableCellDropdown
                onOpen={() => track('ACTION > CELL ACTIONS', { 'Page Title': 'MANAGE ORDERS' })}
                className={Style['width-x-mini']}
                disabled={order.status === 'unknown'}
            >
                <ListDropdownItems order={order} onChangeAction={onChangeAction} />
            </TableCellDropdown>
        )
    };

    return (
        <Table.Tr
            active={isChecked}
            onSelected={(event, data) => onSelected({ event, data }, order)}
            className={`${Style['tr-body']} ${order.attendee_count === order.attendees_deleted_count ? Style['tr-deleted'] : ''} ${
                order.status === 'unknown' ? Style['tr-unknown'] : ''
            }`}
            onClick={() => history.push(`${prefix}/${order.pk}`)}
        >
            {columns.map(column => (
                <React.Fragment key={column.key}>{cells[column.key]()}</React.Fragment>
            ))}
        </Table.Tr>
    );
});

function HeaderRow({ active = false, ordering, onChangeSort = () => {}, columns = [] }) {
    const { t } = useTranslation();

    const cells = {
        [Columns.SOLD_DATE]: column => (
            <SortDropdown
                name="sold_date"
                ordering={ordering}
                label={column.label}
                items={[
                    <DropdownItem.Icons key={1} item={t('ticket.common.ordering.descending')} checked={ordering === '-sold_date'} onClick={() => onChangeSort('-sold_date')} />,
                    <DropdownItem.Icons key={2} item={t('ticket.common.ordering.ascending')} checked={ordering === 'sold_date'} onClick={() => onChangeSort('sold_date')} />
                ]}
            />
        ),
        [Columns.BUYER]: column => (
            <SortDropdown
                name="buyer_last_name"
                ordering={ordering}
                label={column.label}
                items={[
                    <DropdownItem.Icons key={1} item={t('ticket.common.ordering.A-Z')} checked={ordering === 'buyer_last_name'} onClick={() => onChangeSort('buyer_last_name')} />,
                    <DropdownItem.Icons key={2} item={t('ticket.common.ordering.Z-A')} checked={ordering === '-buyer_last_name'} onClick={() => onChangeSort('-buyer_last_name')} />
                ]}
            />
        ),
        [Columns.ORDER_ID]: column => <th>{column.label}</th>,
        [Columns.ORDER]: column => (
            <SortDropdown
                name="transaction_reference"
                ordering={ordering}
                label={column.label}
                items={[
                    <DropdownItem.Icons
                        key={1}
                        item={t('ticket.common.ordering.descending')}
                        checked={ordering === '-transaction_reference'}
                        onClick={() => onChangeSort('-transaction_reference')}
                    />,
                    <DropdownItem.Icons
                        key={2}
                        item={t('ticket.common.ordering.ascending')}
                        checked={ordering === 'transaction_reference'}
                        onClick={() => onChangeSort('transaction_reference')}
                    />
                ]}
            />
        ),
        [Columns.QUANTITY]: column => (
            <SortDropdown
                className={Style['width-mini']}
                name="quantity"
                ordering={ordering}
                label={column.label}
                items={[
                    <DropdownItem.Icons key={1} item={t('ticket.common.ordering.descending')} checked={ordering === '-quantity'} onClick={() => onChangeSort('-quantity')} />,
                    <DropdownItem.Icons key={2} item={t('ticket.common.ordering.ascending')} checked={ordering === 'quantity'} onClick={() => onChangeSort('quantity')} />
                ]}
            />
        ),
        [Columns.BUYING_PRICE]: column => <th className={Style['width-mini']}>{column.label}</th>,
        [Columns.PAYMENT_TYPE]: column => <th className={Style['width-mini']}>{column.label}</th>,
        [Columns.LANGUAGE]: column => <th>{column.label}</th>,
        [Columns.STATUS]: column => <th className={Style['width-x-mini']}>{column.label}</th>,
        [Columns.ACTIONS]: column => <th className={Style['width-x-mini']}>{column.label}</th>
    };

    return (
        <Table.Tr isHeader active={active} className={Style['tr-header']}>
            {columns.map(column => (
                <React.Fragment key={column.key}>{cells[column.key](column)}</React.Fragment>
            ))}
        </Table.Tr>
    );
}

const optionsStatus = [
    {
        key: 'valid',
        text: <Trans i18nKey="attendee.order.status.valid" />,
        name: 'valid'
    },
    {
        key: 'reservation',
        text: <Trans i18nKey="attendee.order.status.reservation" />,
        name: 'reservation'
    },
    {
        key: 'deleted',
        text: <Trans i18nKey="attendee.order.status.deleted" />,
        name: 'deleted'
    },
    {
        key: 'refunded_ticket_office',
        text: <Trans i18nKey="attendee.order.status.refunded_ticket_office" />,
        name: 'refunded_ticket_office'
    },
    {
        key: 'unknown',
        text: <Trans i18nKey="attendee.order.status.unknown" />,
        name: 'unknown'
    }
];

export const spec = {
    // doc in spec const object from src/pages/attendee/list/list.js
    multiple: ['origin__in', 'origin_detail', 'order__status__in', 'code_promo_in', 'attendee__status__in', 'products__variant__in', 'payment__type__in'],
    fields: ['sold_date', 'origin__in', 'origin_detail', 'order__status__in', 'code_promo_in', 'search', 'products__variant__in', 'has_refund_requests', 'payment__type__in'],
    aliases: {
        ticket_origin: 'origin__in',
        ticket_origin_detail: 'origin_detail'
    },
    date: ['sold_date']
};

export function OrderList() {
    const [{ event, organization }] = useAppContext();
    const [, setManagerContext, removeFromContext] = useManagerContext();
    const { t } = useTranslation();
    const { track } = useSegment();
    const locale = i18n.language;
    const mixpanel = useMixpanel();
    const history = useHistory();

    const [pageSize, setPageSize] = usePageSize(50);
    const [currentPage, setCurrentPage] = useCurrentPage(0);
    const [ordering, setOrdering] = useSort('-sold_date');

    const [filters, setFilters] = useFilters(spec);
    const [crossSelling, loadingCrossSelling, syncCrossSelling] = useCrossSelling(true); // eslint-disable-line
    const [originsDetails, loadingOriginsDetails, syncOriginsDetails, optionsOriginDetails, optionsDiscount] = useOriginDetails(); // eslint-disable-line
    const [selectedOrders, setSelectedOrders] = React.useState({});
    const [allSelected, setAllSelected] = React.useState(false);
    const [showExportAttendeeModal, setShowExportAttendeeModal] = React.useState(false);
    const [showExportOrderModal, setShowExportOrderModal] = React.useState(false);

    const [action, setAction] = React.useState(initAction);

    const prefix = React.useMemo(() => {
        return `/ticket/O${organization.id}/E${event.id}/manager`;
    }, [event, organization]);

    const toast = React.useContext(ToastContext);

    // We will need to know if we are in the first Render of the component to know if we have to set
    // initialFilters or if the filters are already touched
    const isFirstRender = useFirstRender();
    const filtersQuery = React.useMemo(
        () =>
            // If we are in the first render and we don't have filters already set for this tab : set InitialFilters
            isFirstRender && !Object.keys(filters).some(key => spec.fields.includes(key)) ? initialFilters : filters,
        [filters]
    );

    // Main orders request
    const [orders, loading, sync, , totalCount, loadingCount] = Order.useApiModel(
        {
            eventId: event.pk,
            organizationId: organization.pk,
            withCount: true,
            query: {
                expand: 'refund_requests',
                ordering: ordering,
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filtersQuery, true)
            },
            onError: () => toast.error(t('ticket.manager.orders.toast.error.retrieval_error')),
            onSuccess: res => updateContextPagination('orders', { pageSize, ordering, currentPage }, res, setManagerContext, removeFromContext),
            onSuccessCount: res => updateContextPagination('orders', { pageSize, ordering, currentPage }, res, setManagerContext, removeFromContext)
        },
        [pageSize, currentPage, ordering, filtersQuery, spec, setManagerContext, removeFromContext]
    );

    useTrackingPage('VIEW - MANAGE ORDER', { 'Page Title': 'MANAGE ORDERS' });

    const handleTrackFilters = React.useCallback(action => track(`ACTION > FILTER ${action}`, { 'Page Title': 'MANAGE ORDERS' }), [track]);

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );

    const onChangeSort = ordering => {
        setCurrentPage(0);
        setOrdering(ordering);
    };

    const handleElementsList = React.useCallback(
        ({ event, data: { checked } }, order) => {
            event.stopPropagation();
            setSelectedOrders(currentState => {
                const newSelected = { ...currentState };
                const isSelected = order.pk in currentState;
                if (!checked && isSelected) {
                    delete newSelected[order.pk];
                }

                if (checked && !isSelected) {
                    newSelected[order.pk] = order;
                }
                setAllSelected(Object.values(newSelected).length === orders.length);

                return newSelected;
            });
        },
        [orders]
    );

    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            if (checked) {
                setAllSelected(true);
                let ordersTmp = {};
                orders?.forEach(order => {
                    ordersTmp[order.pk] = order;
                });
                setSelectedOrders(ordersTmp);
            } else {
                setAllSelected(false);
                setSelectedOrders({});
            }
        },
        [orders]
    );

    const resetSelected = React.useCallback(() => {
        setAllSelected(false);
        setSelectedOrders({});
    }, []);

    const isLoadingFilters = React.useMemo(() => {
        return loading || loadingOriginsDetails || loadingCrossSelling;
    }, [loading, loadingOriginsDetails, loadingCrossSelling]);

    const optionsCrossSelling = React.useMemo(() => {
        let variants = [];
        crossSelling?.map(product => {
            product.variants.forEach(variant => {
                variants.push({
                    text: `${product.name} - ${variant.name}`,
                    name: variant.id.toString(),
                    key: variant.id.toString()
                });
            });
        });
        return variants;
    }, [crossSelling]);

    const optionsRefundRequest = [
        {
            key: 'has_refund_request',
            text: t('attendees.filters.hasRefundRequest'),
            name: 'True'
        },
        {
            key: 'has_not_refund_request',
            text: t('attendees.filters.hasNotRefundRequest'),
            name: 'False'
        }
    ];

    const optionsPaymentType = PaymentTypesList.map(pt => {
        return {
            key: pt,
            text: GetPaymentTypeLabel(pt, t, true),
            name: pt,
            value: pt
        };
    });

    const { columns, views } = useColumns();

    const onCloseAction = React.useCallback(() => {
        setAction(initAction);
        resetSelected();
    }, [resetSelected]);

    const isEmailingBlocked = useIsEmailingBlocked();

    return (
        <>
            <AttendeeExportModal
                organization={organization}
                event={event}
                showExportModal={showExportAttendeeModal}
                setShowExportModal={setShowExportAttendeeModal}
                context="attendees"
                from="orderList"
                ordering={ordering}
                selected={selectedOrders}
                filtersQs={Object.entries(handleSearchQuery(spec, filters))
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&')}
            />
            <OrderExportModal
                organization={organization}
                event={event}
                showExportModal={showExportOrderModal}
                setShowExportModal={setShowExportOrderModal}
                from="orderList"
                ordering={ordering}
                selected={selectedOrders}
                filtersQs={Object.entries(handleSearchQuery(spec, filters))
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&')}
            />
            <ColumnsConfigurator {...views.columnsConfiguratorProps} width={500} />
            <WeezHeader
                title={t('ticket.manager.title')}
                rightComponent={[
                    <Button
                        primary
                        key="top-cta-form"
                        icon={<Icon name={'plus'} margin={false} />}
                        label={t('ticket.manager.addAttendees')}
                        onClick={() => {
                            track('ACTION > ADD ATTENDEES', { 'Page Title': 'MANAGE ORDERS' });
                            history.push(`/ticket/O${organization.id}/E${event.id}/attendees/add`);
                        }}
                    />,
                    <ButtonDropdown
                        key={'dropdown'}
                        renderWhenClosed
                        trigger={
                            <Button
                                onClick={() => track('ACTION > ACTION MANAGE ORDERS', { 'Page Title': 'MANAGE ORDERS' })}
                                inverted
                                primary
                                icon={<Icon name="chevron-bottom" />}
                                label={t('ticket.common.cta.actions')}
                            />
                        }
                        items={[
                            <Dropdown.Item
                                key="export attendees"
                                onClick={() => {
                                    setShowExportAttendeeModal(true);
                                    track('ACTION > EXPORT ATTENDEE LIST', { 'Page Title': 'MANAGE ORDERS' });
                                }}
                                item={t('ticket.manager.attendees.action.export.button')}
                            />,
                            <Dropdown.Item
                                key="export order"
                                onClick={() => {
                                    setShowExportOrderModal(true);
                                    track('ACTION > EXPORT ORDER LIST', { 'Page Title': 'MANAGE ORDERS' });
                                }}
                                item={t('ticket.manager.orders.action.export.button')}
                            />
                        ]}
                    />
                ]}
            >
                {t('ticket.manager.subtitle')}
            </WeezHeader>
            <ChartedEvent event={event}>
                <Message title={t('ticket.manager.attendees.messageViewMapTitle')} type="default">
                    <span>
                        <Link
                            className={Style['link-view-map']}
                            to={`/ticket/O${organization.pk}/E${event.pk}/seating/attendees/manage?display=map`}
                            onClick={() => {
                                mixpanel.track('GDP - Access Seatmap View', {
                                    'Page Title': 'GDP'
                                });
                            }}
                        >
                            {t('ticket.common.clickHere')}
                        </Link>
                        {` ${t('ticket.manager.attendees.messageViewMapContent')}`}
                    </span>
                </Message>
            </ChartedEvent>
            <Tab
                panes={[
                    {
                        menuItem: t('ticket.manager.attendees.titleTab'),
                        menuIcon: 'user',
                        url: `${prefix}/attendees`,
                        key: 'attendees'
                    },
                    {
                        menuItem: t('ticket.manager.orders.titleTab'),
                        menuIcon: 'package',
                        url: `${prefix}/orders`,
                        key: 'orders'
                    }
                ]}
                activeTab={1}
                onTabSelected={tab => {
                    if (tab.key === 'attendees') {
                        track('ACTION > TAB MANAGE ATTENDEES', { 'Page Title': 'MANAGE ORDERS' });
                    }

                    //reset pagination when we click on tab
                    setManagerContext(ctx => {
                        delete ctx['page'];
                        delete ctx['size'];
                        delete ctx['sort'];
                        return { ...ctx };
                    });
                    history.push(tab.url);
                }}
            >
                <ActionContainer sync={() => sync({ allow_cache: false, cache: false })} onClose={onCloseAction} {...action} />
                <Card className={Style['list-container']}>
                    <ContentCard>
                        <FilterSetController
                            total={totalCount}
                            isLoadingTotal={loading || loadingCount}
                            labelTotal={t('ticket.common.order', { count: totalCount })}
                            labelReset={t('ticket.common.action.reset')}
                            lebelFilters={t('ticket.common.cta.filters')}
                            onOpen={() => track('ACTION > FILTER', { 'Page Title': 'MANAGE ORDERS' })}
                            isLoading={isLoadingFilters}
                            onChange={values => {
                                if (filters.search !== undefined && filters.search.length > 0) {
                                    values.search = filters.search;
                                } else {
                                    delete values.search;
                                }
                                setFilters(values);
                                resetSelected();
                                // At the first render we don't want to set the page to 0, because a page can be specified in the url.
                                // Otherwise each time we change the filter, we reset the page to 0
                                if (!isFirstRender) {
                                    setCurrentPage(0);
                                }
                            }}
                            search={{
                                onChange: (_, value) => {
                                    onChangeSearch(value);
                                    resetSelected();
                                },
                                handleReset: (_, value) => {
                                    onChangeSearch(null);
                                },
                                placeholder: t('ticket.common.action.search'),
                                value: filters?.search || '',
                                rightComponent: <ColumnsConfiguratorTrigger {...views.triggerProps} />
                            }}
                            // use Initial Values to set filters Values form initialFilter or filters that are coming
                            // from another tab
                            initialValues={() => {
                                // If we are in the first render, we want to set initialFilters for this tab
                                // We check if there's already a filter selected for this tab and if not we merge
                                // filters to keep values from other tab with initialFilters
                                if (isFirstRender && !Object.keys(filters).some(key => spec.fields.includes(key))) {
                                    return { ...filters, ...initialFilters };
                                    // else we just check if we have filters already set, otherwise we set initialFilters
                                } else {
                                    return Object.keys(filters).length > 0 ? filters : initialFilters;
                                }
                            }}
                            // default Values is set only for reset function. It will reset to initialFilters and not
                            // with all the filters selected through other tabs
                            defaultValues={initialFilters}
                            onReset={() => {}}
                        >
                            <Filter
                                name="origin__in"
                                labelName={t('ticket.common.origin')}
                                multiple
                                options={optionsOrigin}
                                index="1"
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelCTA={t('ticket.common.action.apply')}
                                onPostChange={() => handleTrackFilters('ORIGIN')}
                            />
                            {optionsOriginDetails && optionsOriginDetails.length > 0 && (
                                <Filter
                                    name="origin_detail"
                                    labelName={t('ticket.common.distributor')}
                                    multiple
                                    options={optionsOriginDetails}
                                    index="2"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters('DISTRIBUTOR')}
                                />
                            )}
                            <Filter
                                name="order__status__in"
                                labelName={t('attendees.filters.status')}
                                multiple
                                options={optionsStatus}
                                index="3"
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelCTA={t('ticket.common.action.apply')}
                                onPostChange={() => handleTrackFilters('STATUS')}
                            />
                            <Filter
                                date
                                index="4"
                                name="sold_date"
                                locale={locale}
                                timeStep={5}
                                timezone={event.timezone}
                                labelCTA={t('ticket.common.action.apply')}
                                labelName={t('ticket.manager.filters.order_date')}
                                labelAfter={t('ticket.manager.filters.date.after')}
                                labelBefore={t('ticket.manager.filters.date.before')}
                                labelBetween={t('ticket.manager.filters.date.between')}
                                labelSelect={t('ticket.manager.filters.date.select')}
                                labelDate={t('ticket.manager.filters.date.date')}
                                labelHour={t('ticket.manager.filters.date.hour')}
                                labelStartDate={t('ticket.manager.filters.date.start_date')}
                                labelStartHour={t('ticket.manager.filters.date.start_hour')}
                                labelEndDate={
                                    <LabelEndDateFilter label={t('ticket.manager.filters.date.end_date')} tooltipLabel={t('ticket.manager.filters.order_date_tooltip')} />
                                }
                                labelEndHour={t('ticket.manager.filters.date.end_hour')}
                                customLabelEndDate={
                                    <LabelEndDateFilter label={t('ticket.manager.filters.date.end_date')} tooltipLabel={t('ticket.manager.filters.order_date_tooltip')} />
                                }
                                onPostChange={() => handleTrackFilters('SOLD DATE')}
                            />
                            {optionsDiscount.length > 0 && (
                                <Filter
                                    name="code_promo_in"
                                    labelName={t('attendees.filters.discount')}
                                    multiple
                                    options={optionsDiscount}
                                    index="5"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters('DISCOUNT')}
                                />
                            )}
                            {optionsCrossSelling.length > 0 && (
                                <Filter
                                    name="products__variant__in"
                                    labelName={t('ticket.common.cross_selling')}
                                    multiple
                                    options={optionsCrossSelling}
                                    index="6"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters('CROSS SELLING')}
                                />
                            )}
                            <Filter
                                name="has_refund_requests"
                                labelName={t('attendees.filters.has_refund_requests')}
                                options={optionsRefundRequest}
                                index="7"
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelCTA={t('ticket.common.action.apply')}
                                onPostChange={() => handleTrackFilters('HAS REFUND REQUESTS')}
                            />
                            <Filter
                                name="payment__type__in"
                                labelName={t('ticket.export.headers.payment_type')}
                                multiple
                                options={optionsPaymentType}
                                index="8"
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelCTA={t('ticket.common.action.apply')}
                                onPostChange={() => handleTrackFilters('PAYMENT TYPE')}
                            />
                        </FilterSetController>
                        <div className={Style['global-pagesize-container']}>
                            <SelectButton
                                tooltipTitle={t('ticket.manager.orders.list.no_action_tooltip')}
                                onOpen={() => track('ACTION > ACTION ON SELECTION', { 'Page Title': 'MANAGE ORDERS' })}
                                title={
                                    Object.values(selectedOrders).length > 0
                                        ? t('ticket.manager.orders.list.action', { count: Object.values(selectedOrders).length })
                                        : t('ticket.common.action.bulk')
                                }
                                objects={Object.values(selectedOrders)}
                            >
                                {actionList.map(modelAction => (
                                    <ActionDropdownItem
                                        key={modelAction.NAME}
                                        modelAction={modelAction}
                                        onChangeAction={setAction}
                                        items={Object.values(selectedOrders)}
                                        type="Multiple"
                                        isEmailingBlocked={isEmailingBlocked}
                                    />
                                ))}
                            </SelectButton>
                            <DropdownPageSize
                                loading={loading}
                                current={pageSize}
                                onOpen={() => track('ACTION > SEE MORE ORDERS', { 'Page Title': 'MANAGE ORDERS' })}
                                onChange={newValue => setPageSize(newValue)}
                                pageSizes={[10, 25, 50, 100]}
                                onChangeCurrentpage={setCurrentPage}
                                labelItem="ticket.manager.orders.list.pageSize"
                                totalCount={+totalCount}
                            />
                        </div>
                    </ContentCard>
                    <Table useNew selectable onSelectAll={(event, data) => handleAllClick({ event, data })} className={Style['list__table']}>
                        <Table.THead>
                            <HeaderRow active={allSelected} ordering={ordering} onChangeSort={onChangeSort} columns={columns} />
                        </Table.THead>
                        {!loading && orders && (
                            <Table.TBody>
                                {orders.map((order, index) => (
                                    <OrderRow
                                        key={order.pk}
                                        index={index}
                                        order={order}
                                        isChecked={Boolean(selectedOrders[order.pk])}
                                        onSelected={handleElementsList}
                                        onChangeAction={setAction}
                                        columns={columns}
                                    />
                                ))}
                            </Table.TBody>
                        )}
                    </Table>
                    <LoadingTable loading={loading} />
                    {!loading && orders && !orders.length && totalCount !== '-' && (
                        <Card.EmptyState
                            title={t('ticket.manager.orders.list.no_orders')}
                            subtitle={t('ticket.manager.orders.list.no_orders_subtitle')}
                            icon={<Icon name="user" />}
                        />
                    )}
                    <div style={{ textAlign: 'center', margin: '10px 0' }}>
                        <Paginator
                            currentPage={currentPage}
                            onPageChange={numberPage => {
                                setCurrentPage(numberPage);
                                resetSelected();
                            }}
                            totalCount={+totalCount}
                            pageSize={pageSize}
                        />
                    </div>
                </Card>
            </Tab>
        </>
    );
}
